import { useRef, useState } from "react";
import { strings } from "@remi/localization";
import { RemiButton, RemiInput, useEffect } from "@remi/next-ui";

type MfaChallengeBackupCodeProps = {
  onVerify: (code: string) => void;
  onTryAnotherMethod?: () => void;
  resetError: () => void;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
};

const copy = strings.shared.auth.mfa.challengeBackupCode;

export const MfaChallengeBackupCode = ({
  onVerify,
  onTryAnotherMethod,
  resetError,
  loading,
  error,
  errorMessage,
}: MfaChallengeBackupCodeProps) => {
  const [code, setCode] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || loading) {
      return;
    }
    onVerify(trimmed);
  };

  const showError = error && Boolean(errorMessage);

  return (
    <div className="flex flex-col gap-6" data-testid="mfa-challenge-backup-code">
      <div className="flex flex-col gap-1">
        <h2 ref={headingRef} tabIndex={-1} className="font-semibold text-lg text-text-primary">
          {copy.title}
        </h2>
        <p className="text-sm text-text-secondary">{copy.description}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <RemiInput
            value={code}
            onChange={(e) => {
              if (error) {
                resetError();
              }
              setCode(e.target.value);
            }}
            placeholder={copy.placeholder}
            disabled={loading}
            error={error}
            size="lg"
            autoComplete="off"
            className="w-full sm:w-full sm:min-w-0"
            aria-label={copy.placeholder}
            aria-invalid={error || undefined}
            data-testid="mfa-backup-code-input"
          />
          {showError && (
            <span
              role="alert"
              className="text-[10px] text-text-destructive"
              data-testid="mfa-backup-code-error-message"
            >
              {errorMessage}
            </span>
          )}
        </div>
        <RemiButton
          type="submit"
          variant="primary"
          size="md"
          disabled={!code.trim() || loading}
          loading={loading}
          fullWidth
          data-testid="mfa-backup-code-submit"
        >
          {copy.submit}
        </RemiButton>
        {onTryAnotherMethod && (
          <RemiButton
            variant="tertiary"
            size="sm"
            onClick={onTryAnotherMethod}
            className="-ml-2 self-start text-text-secondary"
            data-testid="mfa-try-another-method"
          >
            Try another method
          </RemiButton>
        )}
      </form>
    </div>
  );
};
